import React from 'react';
import { navigateTo } from '@/lib/navigate';

// ============================================================================
// hydraLinks — SSoT РЕНДЕРЕРА ССЫЛОК (Этап 5+, решение пользователя):
// ОДИН парсер и ОДИН компонент для всех ссылок платформы (справка, чаты,
// markdown-документы, журналы). Никаких локальных обработчиков по месту.
//
// Поддерживаемые схемы:
//   doc:<id>                        → раздел справки (/designer/docs?id=)
//   object:<type>/<code>[?id=...]   → объект метаданных (список или запись)
//       type: catalog|document|register|task|order
//   /designer/... , /enterprise/... → внутренний маршрут (роутер)
//   http(s)://...                   → внешняя (новая вкладка)
// ============================================================================

export interface HydraLinkTarget {
    kind: 'doc' | 'object' | 'route' | 'external';
    /** исходный href */
    href: string;
    /** для doc — id раздела; для route — путь; для external — url */
    value: string;
    /** для object */
    objectType?: string;
    objectCode?: string;
    objectId?: string;
}

const OBJECT_TYPES = ['catalog', 'document', 'register', 'task', 'order'];

export function parseHydraLink(href: string): HydraLinkTarget {
    const h = (href || '').trim();
    if (h.startsWith('doc:')) return { kind: 'doc', href: h, value: h.slice(4) };
    const om = h.match(/^object:([a-z]+)\/([^/?#]+)(?:\?id=([^&#]+))?$/i);
    if (om && OBJECT_TYPES.includes(om[1].toLowerCase())) {
        return { kind: 'object', href: h, value: h, objectType: om[1].toLowerCase(), objectCode: decodeURIComponent(om[2]), objectId: om[3] ? decodeURIComponent(om[3]) : undefined };
    }
    if (h.startsWith('/') ) return { kind: 'route', href: h, value: h };
    if (/^https?:\/\//i.test(h)) return { kind: 'external', href: h, value: h };
    // относительное без схемы — трактуем как маршрут приложения
    return { kind: 'route', href: h.startsWith('#') ? h.slice(1) : h, value: h };
}

/** Навигация по цели ссылки (единая точка). */
export function followHydraLink(t: HydraLinkTarget): void {
    switch (t.kind) {
        case 'doc':
            // роут зарегистрирован ВНУТРИ /designer (как все страницы конфигуратора)
            navigateTo(`/designer/docs?id=${encodeURIComponent(t.value)}`);
            break;
        case 'object': {
            const base = `/designer/object/${t.objectType}/${t.objectCode}`;
            navigateTo(t.objectId ? `${base}?id=${encodeURIComponent(t.objectId)}` : base);
            break;
        }
        case 'route':
            navigateTo(t.value);
            break;
        case 'external':
            window.open(t.value, '_blank', 'noopener');
            break;
    }
}

/**
 * <HydraLink> — ЕДИНСТВЕННЫЙ компонент ссылок платформы.
 * children — содержимое; стили — темизируемые (primary underline).
 */
export function HydraLink({ href, children }: { href: string; children?: React.ReactNode }) {
    const target = parseHydraLink(href);
    const onClick = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        followHydraLink(target);
    };
    return (
        <a href={target.href} onClick={onClick} className="text-primary underline underline-offset-2 hover:opacity-80">
            {children}
        </a>
    );
}

/** Регэксп «ссылкообразных» кусков в ПРОСТОМ тексте (сообщения чата, журнал):
 *  [текст](цель) · doc:id · object:type/code[?id=] · http(s)://... */
const LINK_RE = /\[([^\]]+)\]\(([^)\s]+)\)|(doc:[A-Za-z0-9_-]+)|(object:[a-z]+\/[^\s,;)"]+(?:\?id=[^\s,;)"]+)?)|(https?:\/\/[^\s<>"']+)/gi;

export interface TextToken { text: string; link?: string }

/** Разбор простого текста на токены (текст | ссылка) — SSoT для линковки. */
export function tokenizeTextLinks(text: string): TextToken[] {
    const out: TextToken[] = [];
    let rest = String(text ?? '');
    while (rest.length) {
        const m = rest.match(LINK_RE);
        if (!m || m.index === undefined || m.index === 0 && !m[0]) { out.push({ text: rest }); break; }
        if (m.index > 0) out.push({ text: rest.slice(0, m.index) });
        // какой альтернативе соответствует совпадение
        if (m[1] !== undefined) out.push({ text: m[1], link: m[2] });
        else out.push({ text: m[0], link: m[0] });
        rest = rest.slice(m.index + m[0].length);
    }
    return out;
}

/** Готовый рендер простого текста со ссылками (SSoT). */
export function RichText({ text }: { text: string }) {
    const tokens = tokenizeTextLinks(text);
    return (
        <>
            {tokens.map((t, i) =>
                t.link
                    ? <HydraLink key={i} href={t.link}>{t.text}</HydraLink>
                    : <span key={i}>{t.text}</span>
            )}
        </>
    );
}
